import { AMC_PLANS_API, generateBFFUrl } from '@/configs/apiEndPoints';
import { GenericError } from '@/errors/GenericServerError';
import { AxiosError, AxiosResponse } from 'axios';
import axiosInstance from './axiosConfig';
import generateHttpHeaders from './axiosConfig/generateHttpHeaders';
import { PlanCardProps } from '@/components/PlanCards';
import { BenefitType } from '@/components/PlanCards/utils';
import { ASSET_ID_QUERY_PARAM } from '@/configs/serviceConstants';
import { ValidityType } from '@/utils/plancardsUtils';

interface IBenefits {
  id: string;
  category: any;
  description: string;
  icon: string;
  value: { [key: string]: any };
  show: boolean;
  allowViewAllCTA: boolean;
}

interface IPricePlan {
  id: string;
  tier: string;
  price: number;
  recommended: boolean;
  show: boolean;
  allowViewAllCTA: boolean;
  validity: ValidityType;
}

export const fetchAmcPlans = (
  assetId: string,
  selectedDeviceVariantId: string,
  serverCookie?: any,
) => {
  const url = generateBFFUrl(AMC_PLANS_API);
  const amcData = axiosInstance
    .get(url, {
      headers: { ...generateHttpHeaders(serverCookie), 'app-version': '200' },
      params: {
        product_id: selectedDeviceVariantId,
        [ASSET_ID_QUERY_PARAM]: assetId,
      },
    })
    .then(({ data }) => {
      let mobileData: {
        pricePlans: IPricePlan[];
        benefits: IBenefits[];
      } = { pricePlans: [], benefits: [] };

      let webData: PlanCardProps[] = [];

      data.forEach(
        (plan: {
          id: string;
          tier: string;
          price: {
            originalPrice: number;
            discount: {
              value: number;
              unit: string;
            };
            discountedPrice: number;
          };
          recommended: any;
          show: boolean;
          allowViewAllCTA: boolean;
          validity: ValidityType;
          duration: string;
          benefits: BenefitType[];
          mobileBenefits: IBenefits[];
        }) => {
          webData.push({
            id: plan.id,
            tier: plan.tier,
            price: plan?.price?.discountedPrice,
            validity: plan.validity,
            recommended: !!plan.recommended,
            show: plan.show,
            benefits: plan.benefits,
            allowViewAllCTA: plan.allowViewAllCTA,
          });
          mobileData.pricePlans.push({
            id: plan.id,
            tier: plan.tier,
            price: plan.price?.discountedPrice,
            recommended: !!plan.recommended,
            show: plan.show,
            allowViewAllCTA: plan.allowViewAllCTA,
            validity: plan.validity,
          });
          plan.mobileBenefits.forEach((benefit: IBenefits) => {
            const item = mobileData.benefits.find((x) => x.id === benefit.id);
            if (item) {
              item.value = {
                ...item.value,
                [plan.tier]: benefit.value,
              };
            } else {
              mobileData.benefits.push({
                id: benefit.id,
                category: benefit.category,
                description: benefit.description,
                icon: benefit.icon,
                value: { [plan.tier]: benefit.value },
                show: plan.show,
                allowViewAllCTA: plan.allowViewAllCTA,
              });
            }
          });
        },
      );
      return {
        webData,
        mobileData,
      };
    })
    .catch((error: AxiosError) => {
      console.log(error, 'amc plans');
      const { response } = error;
      const { data, status, statusText } = response as AxiosResponse;
      throw new GenericError('Something went wrong', status, statusText, data);
    });
  return amcData;
};

type Validity = {
  duration: number;
  unit: string;
};

type Discount = {
  value: number;
  unit: string;
};

type Price = {
  originalPrice: number;
  discount: Discount;
  discountedPrice: number;
};

type Benefit = {
  id: number;
  desc: string;
  icon: string;
};

type MobileBenefit = {
  id: string;
  category: {
    title: string;
    subtitle: string;
  };
  description: string;
  icon: string;
  value: string | number;
};

type CartBenefit = {
  id: string;
  icon: string;
  title: string;
  description: string;
};

export type AMCPlan = {
  id: string;
  tier: string;
  validity: Validity;
  duration: number;
  price: Price;
  benefits: Benefit[];
  mobileBenefits: MobileBenefit[];
  cartBenefits: CartBenefit[];
  recommended: boolean;
  show: boolean;
  allowViewAllCTA: boolean;
};

export const fetchAMCPlans = async (
  assetId: string,
  selectedDeviceVariantId: string,
  serverCookie?: any,
): Promise<AMCPlan[]> => {
  try {
    const response = await axiosInstance.get(generateBFFUrl(AMC_PLANS_API), {
      headers: { ...generateHttpHeaders(serverCookie), 'app-version': '200' },
      params: {
        product_id: selectedDeviceVariantId,
        [ASSET_ID_QUERY_PARAM]: assetId,
      },
    });

    return response?.data ?? {};
  } catch (error: any) {
    const { response } = error;
    const { data, status, statusText } = response as AxiosResponse;
    throw new GenericError('Something went wrong', status, statusText, data);
  }
};
